import { Component } from 'react';

/**
 * App-wide React error boundary. Catches any runtime error thrown during
 * render of its children and shows a clean "Something went wrong" fallback
 * card instead of letting the page blank-out white.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <AppRoutes />
 *   </ErrorBoundary>
 *
 * The boundary auto-resets when the user navigates (via window.location
 * change events), so a bad route doesn't permanently jam the app.
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] caught render error:', error, info);
    this.setState({ info });
  }

  handleRefresh = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  /**
   * Nuclear option: unregister every service worker, dump every cache,
   * clear localStorage + sessionStorage, then hard-reload. This is the
   * one-tap escape hatch for users stranded on a stale deploy where
   * their SW + HTTP cache combo is repeatedly serving a broken bundle.
   * Safe to call from a single button click — it won't run unless the
   * user explicitly taps it.
   */
  handleForceReset = async () => {
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      try { window.localStorage.clear(); } catch { /* ignore quota errors */ }
      try { window.sessionStorage.clear(); } catch { /* ignore quota errors */ }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[ErrorBoundary] reset failed:', e);
    } finally {
      // Cache-busting reload — bypasses HTTP cache too.
      window.location.replace(`/?reset=${Date.now()}`);
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const errStr = (this.state.error && (this.state.error.message || String(this.state.error))) || 'Unknown error';
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface px-4" data-testid="app-error-boundary">
        <div className="max-w-lg w-full bg-white border border-red-200 rounded-lg shadow-sm p-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="text-3xl">⚠️</div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-bold text-red-700 mb-1">Something went wrong</h1>
              <p className="text-sm text-muted-foreground">
                The page hit an unexpected error and couldn't render. Try refreshing the page — if it keeps happening, contact support.
              </p>
            </div>
          </div>
          <details className="mb-4 text-xs">
            <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
              Show technical details
            </summary>
            <pre className="mt-2 p-3 bg-red-50 border border-red-100 rounded text-red-800 overflow-x-auto whitespace-pre-wrap break-words max-h-48">
              {errStr}
            </pre>
          </details>
          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={this.handleRefresh}
              className="px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md hover:opacity-90"
              data-testid="error-refresh-button"
            >
              Refresh page
            </button>
            <button
              type="button"
              onClick={this.handleGoHome}
              className="px-4 py-2 text-sm font-medium border border-border rounded-md hover:bg-muted"
              data-testid="error-home-button"
            >
              Go to home
            </button>
            <button
              type="button"
              onClick={this.handleForceReset}
              className="px-4 py-2 text-sm font-medium border border-red-300 text-red-700 rounded-md hover:bg-red-50"
              data-testid="error-force-reset-button"
              title="Clears the app cache + service worker and reloads. Use this if Refresh keeps showing the same error."
            >
              Force reset &amp; reload
            </button>
          </div>
          <p className="mt-3 text-[11px] text-muted-foreground">
            Stuck on this error after refresh? Tap <b>Force reset &amp; reload</b> — it clears the app&apos;s cached files and pulls a fresh copy.
          </p>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
